/**
 * Billing e-mail for the organisation, on the Subscription settings page.
 *
 * Stripe sends invoices and receipts to this address instead of the one typed
 * at checkout. The server decides who may edit (`can_edit`: the owner); other
 * roles that may read it see the value without a Save button. When the
 * settings cannot be loaded (no agency session, a role without billing
 * access, a network failure) the field is not rendered at all.
 */
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import {
  BillingApiError,
  BillingSettings,
  getBillingSettings,
  updateBillingSettings,
} from '../../../lib/api/billing/billing-api';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import './billingEmailField.scss';

type Notice = { tone: 'success' | 'warning' | 'error'; text: string };

const INPUT_ID = 'billingEmailField-input';

const BillingEmailField = () => {
  const { t } = useTranslation();
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  const hasAgencyToken = Boolean(agencyToken);

  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [value, setValue] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Guards against a second submit before the disabled state has rendered.
  const savingRef = useRef<boolean>(false);
  // The session the visible data belongs to; a reply for another one is dropped.
  const tokenRef = useRef<string>(agencyToken);
  tokenRef.current = agencyToken;

  // Keyed on the token VALUE, not its presence: the admin "login as seller"
  // handoff swaps the agency session mid-page and the field must then show
  // the other organisation's address.
  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setValue('');
    setFieldError(null);
    setNotice(null);
    if (!agencyToken) {
      return undefined;
    }
    getBillingSettings()
      .then((loaded) => {
        if (cancelled) return;
        setSettings(loaded);
        setValue(loaded.billing_email ?? '');
      })
      .catch(() => {
        // Not allowed or not reachable: the field stays hidden.
        if (!cancelled) setSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyToken]);

  if (!hasAgencyToken || !settings) {
    return null;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    const submittedFor = agencyToken;
    setSaving(true);
    setFieldError(null);
    setNotice(null);
    try {
      const result = await updateBillingSettings(value);
      if (tokenRef.current !== submittedFor) return;
      setSettings((current) =>
        current ? { ...current, billing_email: result.billing_email } : current
      );
      setValue(result.billing_email ?? '');
      setNotice({
        tone: result.stripe?.status === 'failed' ? 'warning' : 'success',
        text: result.message || t('BillingEmailSaved'),
      });
    } catch (err) {
      if (tokenRef.current !== submittedFor) return;
      const apiError = err as BillingApiError;
      const invalid = apiError?.fieldErrors?.billing_email?.[0];
      if (apiError?.status === 422) {
        // What was typed stays in the input so it can be corrected.
        setFieldError(invalid || apiError.message || t('BillingEmailInvalid'));
      } else {
        setNotice({
          tone: 'error',
          text: apiError?.message || t('BillingEmailSaveFailed'),
        });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className='billingEmailField' data-testid='billing-email-field'>
      {settings.can_edit ? (
        <form
          className='billingEmailField__form'
          onSubmit={handleSubmit}
          noValidate
          data-testid='billing-email-form'
        >
          <label className='billingEmailField__title' htmlFor={INPUT_ID}>
            {t('BillingEmail')}
          </label>
          <div className='billingEmailField__row'>
            <input
              id={INPUT_ID}
              className={`billingEmailField__input${
                fieldError ? ' billingEmailField__input--invalid' : ''
              }`}
              type='email'
              autoComplete='off'
              maxLength={254}
              placeholder={t('BillingEmailPlaceholder')}
              value={value}
              disabled={saving}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby='billingEmailField-help'
              onChange={(e) => {
                setValue(e.target.value);
                if (fieldError) setFieldError(null);
              }}
            />
            <MainSquareButton
              value={saving ? t('Saving') : t('Save')}
              type='submit'
              disabled={saving}
              loading={saving}
            />
          </div>
          {fieldError && (
            <p
              className='billingEmailField__fieldError'
              role='alert'
              data-testid='billing-email-field-error'
            >
              {fieldError}
            </p>
          )}
          <p className='billingEmailField__help' id='billingEmailField-help'>
            {t('BillingEmailHelp')}
          </p>
        </form>
      ) : (
        <>
          <h3 className='billingEmailField__title'>{t('BillingEmail')}</h3>
          <p
            className='billingEmailField__readonly'
            data-testid='billing-email-readonly'
          >
            {settings.billing_email || t('BillingEmailNotSet')}
          </p>
        </>
      )}
      {notice && (
        <p
          className={`billingEmailField__notice billingEmailField__notice--${notice.tone}`}
          role={notice.tone === 'success' ? 'status' : 'alert'}
          data-testid={`billing-email-notice-${notice.tone}`}
        >
          {notice.text}
        </p>
      )}
    </div>
  );
};

export default BillingEmailField;
